import type { ScaffoldLevel } from './contracts';
import { buildLongMultiplicationWorkTemplate } from './proceduralScaffold';
import type { MathWorksheetProblemV2, MathWorksheetSetV2 } from './worksheetTypes';
import { deriveWorksheetSequenceMetaV2, WORKSHEET_SURFACE_PROFILE_V2 } from './worksheetSurface';

export type TwoDigitMultiplicationSetOptions = {
  worksheetSetId: string;
  fixedMultiplicand?: number;
  multipliers?: number[];
  skillCode?: string;
  generatorVersion?: string;
  contentVersion?: string;
};

const DEFAULT_MULTIPLIERS = [18, 19, 21, 25, 28, 31, 47, 57, 62, 66];
const SCAFFOLD_SEQUENCE: ScaffoldLevel[] = ['S3', 'S2', 'S2', 'S1', 'S1', 'S0', 'S0', 'S0', 'S0', 'S0'];

function placement(indexOnPage: number) {
  const column = indexOnPage % 2;
  const row = Math.floor(indexOnPage / 2);
  return {
    x: column === 0 ? 0.08 : 0.54,
    y: 0.14 + row * 0.27,
    width: 0.38,
    height: 0.22,
  };
}

export function composeTwoDigitMultiplicationSet(
  options: TwoDigitMultiplicationSetOptions,
): MathWorksheetSetV2 {
  const multiplicand = options.fixedMultiplicand ?? 28;
  const multipliers = options.multipliers ?? DEFAULT_MULTIPLIERS;
  if (multipliers.length !== 10) throw new Error('Recovery benchmark expects exactly 10 problems.');
  if (new Set(multipliers).size !== multipliers.length) throw new Error('Multiplier sequence must be unique.');

  const problems: MathWorksheetProblemV2[] = multipliers.map((multiplier, index) => ({
    problemId: `${options.worksheetSetId}-P${String(index + 1).padStart(2, '0')}`,
    skillCode: options.skillCode ?? 'BK-MTH-OPS-MUL-2X2-PILOT',
    operation: 'multiplication',
    multiplicand,
    multiplier,
    difficulty: 'D2',
    scaffold: SCAFFOLD_SEQUENCE[index],
    stage: index === 0 ? 'GUIDED' : index < 5 ? 'CONTROLLED_PRACTICE' : 'INDEPENDENT_PRACTICE',
    reasonCode: 'current_practice',
    workGuideProfile: 'STANDARD',
    placement: placement(index % 5),
    workTemplate: buildLongMultiplicationWorkTemplate(multiplicand, multiplier),
    generatorVersion: options.generatorVersion ?? 'le-v2-set-composer-recovery-1',
    contentVersion: options.contentVersion ?? 'recovery-1',
    sequenceMeta: deriveWorksheetSequenceMetaV2(index, multipliers.length),
  }));

  return {
    worksheetSetId: options.worksheetSetId,
    subjectCode: 'MTH',
    worksheetVersion: 'le-v2-recovery-1',
    mode: 'WORKSHEET',
    feedbackPolicy: 'DELAYED_WORKSHEET',
    surface: WORKSHEET_SURFACE_PROFILE_V2,
    pages: [
      { pageId: `${options.worksheetSetId}-PAGE-1`, pageNumber: 1, problems: problems.slice(0, 5) },
      { pageId: `${options.worksheetSetId}-PAGE-2`, pageNumber: 2, problems: problems.slice(5, 10) },
    ],
  };
}
